import { useCallback } from 'react';

import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { useCanvas } from '../../../../core/components/canvas/hooks/useCanvas.js';
import { useHoneycombTextSize } from '../../../hooks/use-honeycomb-text-size.js';
import { CanvasTextAreaProps } from '../../../types/canvas-text-area.js';
import type { HoneycombLabelsDisplay } from '../../../types/honeycomb.js';
import { drawLabelAndValue } from '../utils/draw-label-and-value.js';
import { fillShape } from '../utils/fill-shape.js';
import { removeTransparentBorder } from '../utils/remove-transparent-border.js';
import { shouldRenderLabels } from '../utils/should-render-labels.js';

interface CanvasSquareRendererProps {
  x: number;
  y: number;
  width: number;
  fillColor: string;
  value: string | number;
  name?: string;
  strokeColor?: 'transparent' | string;
  radius?: number;
  labelsDisplay?: HoneycombLabelsDisplay;
  truncationMode?: TruncationMode;
}

const createRoundedSquarePath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  radius: number,
) => {
  ctx.moveTo(x, y);
  ctx.beginPath();
  ctx.roundRect(x, y, width, width, radius);
};

const getSquareTextArea = (x: number, y: number, width: number) => ({
  x: x + width / 2,
  y: y + width / 2,
  width,
  height: width,
  leftBoundary: x,
  rightBoundary: x + width,
});

export const CanvasSquareRenderer = (props: CanvasSquareRendererProps) => {
  const {
    x,
    y,
    width,
    fillColor,
    strokeColor = fillColor,
    value,
    name = '',
    radius = 0,
    labelsDisplay,
    truncationMode,
  } = props;
  const textSize = useHoneycombTextSize();

  const textArea: CanvasTextAreaProps = getSquareTextArea(x, y, width);

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      createRoundedSquarePath(ctx, x, y, width, radius);
      removeTransparentBorder(ctx, strokeColor);
      fillShape(ctx, fillColor);
      if (shouldRenderLabels(labelsDisplay)) {
        drawLabelAndValue(
          ctx,
          value,
          textArea,
          fillColor,
          name,
          truncationMode,
          labelsDisplay,
          textSize,
        );
      }

      return false;
    },
    [
      x,
      y,
      width,
      fillColor,
      strokeColor,
      labelsDisplay,
      truncationMode,
      name,
      value,
      radius,
      textArea,
      textSize,
    ],
  );

  useCanvas('honeycomb-square', draw);

  return null;
};

CanvasSquareRenderer['displayName'] = 'CanvasSquareRenderer';
